import { Check, ChevronRight, MoreHorizontal } from 'lucide-react';
import moment from 'moment';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import ReminderComposer, { type ReminderComposerSubmit } from '@/components/reminders/ReminderComposer';
import { getReminderAssignees } from '@/components/reminders/reminderHelpers';
import { changedNotifications } from '@/components/notifications/notificationSettings';
import { usePhoneMask } from '@/hooks/usePhoneMask';
import { cn } from '@/lib/utils';
import type { RootState } from '@/reducers';
import {
	type Reminder,
	type SequenceAction,
	useCompleteReminderMutation,
	useUpdateReminderMutation,
} from '@/slices/remindersApi';
import {
	type Enrollment,
	type EnrollmentStep,
	useGetEnrollmentsQuery,
	useStopEnrollmentMutation,
} from '@/slices/reminderSequencesApi';
import {
	ACTION_META,
	ActionIcon,
	formatStepDue,
	overdueAge,
	personName,
	runAddedAutomatically,
	runEndedLabel,
} from './sequenceActions';

export type QuickAction = (action: SequenceAction, reminder: Reminder) => void;

interface CustomerReminderSequencesProps {
	customerId: string;
	customerName?: string;
	/** Call / Text / Email buttons on open steps; hidden without it */
	onQuickAction?: QuickAction;
	/** Shown when the customer isn't on any sequence */
	emptyHint?: React.ReactNode;
}

const isOverdue = (reminder: Reminder) =>
	!reminder.isCompleted && moment(reminder.dateTime).isBefore(moment().startOf('day'));
const isDueToday = (reminder: Reminder) =>
	!reminder.isCompleted && moment(reminder.dateTime).isBefore(moment().endOf('day'));

const StepRow = ({
	step,
	last,
	currentUserId,
	onToggle,
	onEdit,
	onQuickAction,
	busy,
}: {
	step: EnrollmentStep;
	last: boolean;
	currentUserId?: string;
	onToggle: (reminder: Reminder) => void;
	onEdit: (reminder: Reminder) => void;
	onQuickAction?: QuickAction;
	busy: boolean;
}) => {
	const reminder = step.reminder;
	const done = Boolean(reminder?.isCompleted);
	const overdue = reminder ? isOverdue(reminder) : false;
	const quick =
		reminder && !done && isDueToday(reminder) && onQuickAction && ['call', 'text', 'email'].includes(step.action);
	const others = reminder
		? getReminderAssignees(reminder).filter((user) => user._id !== currentUserId)
		: [];

	return (
		<li className="relative flex gap-3 pl-1">
			{/* The line joining the dots */}
			{!last && <span aria-hidden className="absolute left-[15px] top-8 bottom-0 w-px bg-border" />}
			<button
				type="button"
				disabled={!reminder || busy}
				onClick={() => reminder && onToggle(reminder)}
				aria-label={
					!reminder
						? `${step.title} was removed`
						: done
							? `Mark ${step.title} as not done`
							: `Mark ${step.title} as done`
				}
				aria-pressed={done}
				className={cn(
					'relative z-[1] mt-1 flex size-[22px] shrink-0 items-center justify-center rounded-full border-[1.5px] transition-colors touch:before:absolute touch:before:-inset-3 max-md:before:absolute max-md:before:-inset-3',
					done
						? 'border-primary bg-primary text-primary-foreground'
						: overdue
							? 'border-destructive/70 bg-background hover:bg-destructive/10 dark:border-red-400/70'
							: 'border-muted-foreground/40 bg-background hover:border-primary',
					!reminder && 'border-dashed opacity-50',
				)}
			>
				{done && <Check className="size-3" strokeWidth={3} />}
			</button>
			<div className={cn('min-w-0 flex-1 pb-4', last && 'pb-1')}>
				<button
					type="button"
					disabled={!reminder}
					onClick={() => reminder && onEdit(reminder)}
					className="flex w-full min-w-0 items-start gap-2 text-left disabled:cursor-default"
				>
					<ActionIcon action={step.action} size="sm" className="mt-0.5" />
					<span className="min-w-0 flex-1">
						<span
							className={cn(
								'block truncate text-sm font-medium',
								(done || !reminder) && 'text-muted-foreground',
								!reminder && 'line-through',
							)}
						>
							{step.title}
						</span>
						<span
							className={cn(
								'block text-xs text-muted-foreground',
								overdue && 'text-destructive dark:text-red-400',
							)}
						>
							{!reminder
								? 'Removed'
								: done
									? `Done${reminder._completedBy ? ` by ${personName(reminder._completedBy)}` : ''}${
											reminder.completedAt ? ` · ${moment(reminder.completedAt).format('lll')}` : ''
										}`
									: overdue
										? `${overdueAge(reminder.dateTime)} · due ${moment(reminder.dateTime).format('ll')}`
										: `Due ${formatStepDue(reminder.dateTime)}`}
							{reminder && !done && others.length > 0 && ` · ${others.map((user) => personName(user)).join(', ')}`}
						</span>
					</span>
				</button>
				{quick && reminder && (
					<Button
						size="sm"
						variant="outline"
						className="mt-2 h-7 gap-1.5 rounded-full px-3 text-xs touch:h-9 max-md:h-9"
						onClick={() => onQuickAction?.(step.action, reminder)}
					>
						<ActionIcon action={step.action} size="sm" className="-ml-1 size-4 bg-transparent" />
						{ACTION_META[step.action].label}
					</Button>
				)}
			</div>
		</li>
	);
};

const RunCard = ({
	run,
	customerName,
	currentUserId,
	onToggle,
	onEdit,
	onRemove,
	onQuickAction,
	busyId,
}: {
	run: Enrollment;
	customerName: string;
	currentUserId?: string;
	onToggle: (reminder: Reminder) => void;
	onEdit: (reminder: Reminder) => void;
	onRemove: (run: Enrollment) => void;
	onQuickAction?: QuickAction;
	busyId: string | null;
}) => {
	const navigate = useNavigate();
	const active = run.status === 'active';
	const summary = active
		? run.currentStepNumber
			? `Step ${run.currentStepNumber} of ${run.stepCount}`
			: `${run.stepCount} steps`
		: runEndedLabel(run);
	const addedAutomatically = runAddedAutomatically(run);

	return (
		<div className="space-y-3">
			<div className="flex items-center gap-2">
				<div className="min-w-0 flex-1">
					<p className="truncate text-sm font-semibold">{run.sequenceName}</p>
					{addedAutomatically && <p className="truncate text-xs text-muted-foreground">{addedAutomatically}</p>}
				</div>
				<span className="max-w-[60%] shrink-0 truncate text-xs text-muted-foreground">{summary}</span>
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<Button
							variant="ghost"
							size="icon"
							className="h-7 w-7 shrink-0 text-muted-foreground max-md:size-11"
							aria-label={`Actions for ${run.sequenceName}`}
						>
							<MoreHorizontal className="h-4 w-4" />
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent align="end">
						<DropdownMenuItem onSelect={() => navigate(`/reminders/sequences?edit=${run._reminderSequence}`)}>
							Edit sequence
						</DropdownMenuItem>
						{active && (
							<DropdownMenuItem
								className="text-destructive focus:text-destructive"
								onSelect={() => onRemove(run)}
							>
								Remove {customerName} from sequence…
							</DropdownMenuItem>
						)}
					</DropdownMenuContent>
				</DropdownMenu>
			</div>
			<ol aria-label={`${run.sequenceName} steps`}>
				{run.steps.map((step, index) => (
					<StepRow
						key={step.stepId}
						step={step}
						last={index === run.steps.length - 1}
						currentUserId={currentUserId}
						onToggle={onToggle}
						onEdit={onEdit}
						onQuickAction={active ? onQuickAction : undefined}
						busy={busyId === step.reminder?._id}
					/>
				))}
			</ol>
		</div>
	);
};

/**
 * A customer's reminder sequences as timelines: what's done, by whom and
 * when, and when the rest are due. Finished and removed runs fold into one
 * "Earlier" row.
 */
const CustomerReminderSequences = ({
	customerId,
	customerName,
	onQuickAction,
	emptyHint,
}: CustomerReminderSequencesProps) => {
	const maskPhone = usePhoneMask();
	const users = useSelector((state: RootState) => state.users || []);
	const currentUserId = useSelector((state: RootState) => state.auth?.user?._id) as string | undefined;
	const { data, isLoading } = useGetEnrollmentsQuery({ _customer: customerId });
	const [completeReminder] = useCompleteReminderMutation();
	const [updateReminder, { isLoading: saving }] = useUpdateReminderMutation();
	const [stopEnrollment, { isLoading: stopping }] = useStopEnrollmentMutation();
	const [busyId, setBusyId] = useState<string | null>(null);
	const [editing, setEditing] = useState<Reminder | null>(null);
	const [removing, setRemoving] = useState<Enrollment | null>(null);
	const [showFinished, setShowFinished] = useState(false);

	const name = maskPhone(customerName || 'this customer');
	const runs = data?.enrollments ?? [];
	const active = runs.filter((run) => run.status === 'active');
	const finished = runs.filter((run) => run.status !== 'active');

	const toggle = async (reminder: Reminder) => {
		setBusyId(reminder._id);
		try {
			await completeReminder({ _id: reminder._id, isCompleted: !reminder.isCompleted }).unwrap();
		} catch {
			toast.error('Could not update the step');
		} finally {
			setBusyId(null);
		}
	};

	const saveEdit = async (value: ReminderComposerSubmit) => {
		if (!editing) return;
		try {
			await updateReminder({
				_id: editing._id,
				updates: {
					text: value.text,
					dateTime: value.dateTime,
					notes: value.notes,
					priority: value.priority,
					notifications: changedNotifications(editing, 'reminder', value.notifications),
					_assignees: value._assignees,
				},
			}).unwrap();
			toast.success('Step updated');
			setEditing(null);
		} catch {
			toast.error('Could not update the step');
		}
	};

	const confirmRemove = async () => {
		if (!removing) return;
		try {
			await stopEnrollment(removing._id).unwrap();
			toast.success(`${name} removed from ${removing.sequenceName}`);
		} catch {
			toast.error('Could not remove them from the sequence');
		} finally {
			setRemoving(null);
		}
	};

	const openRemaining = removing
		? removing.steps.filter((step) => step.reminder && !step.reminder.isCompleted).length
		: 0;

	if (isLoading) {
		return <p className="px-4 pb-4 text-sm text-muted-foreground">Loading…</p>;
	}

	return (
		<div className="space-y-5 px-4 pb-4">
			{active.length === 0 && (emptyHint ?? null)}
			{active.map((run) => (
				<RunCard
					key={run._id}
					run={run}
					customerName={name}
					currentUserId={currentUserId}
					onToggle={toggle}
					onEdit={setEditing}
					onRemove={setRemoving}
					onQuickAction={onQuickAction}
					busyId={busyId}
				/>
			))}

			{finished.length > 0 && (
				<div className="space-y-4">
					<button
						type="button"
						onClick={() => setShowFinished((value) => !value)}
						aria-expanded={showFinished}
						className="flex w-full items-center gap-2 text-left text-xs text-muted-foreground hover:text-foreground max-md:min-h-11"
					>
						<span className="min-w-0 flex-1 truncate">
							Earlier: {finished.map((run) => run.sequenceName).join(', ')}
						</span>
						<ChevronRight className={cn('size-4 shrink-0 transition-transform', showFinished && 'rotate-90')} />
					</button>
					{showFinished &&
						finished.map((run) => (
							<RunCard
								key={run._id}
								run={run}
								customerName={name}
								currentUserId={currentUserId}
								onToggle={toggle}
								onEdit={setEditing}
								onRemove={setRemoving}
								busyId={busyId}
							/>
						))}
				</div>
			)}

			<AlertDialog open={Boolean(removing)} onOpenChange={(open) => !open && setRemoving(null)}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							Remove {name} from &ldquo;{removing?.sequenceName}&rdquo;?
						</AlertDialogTitle>
						<AlertDialogDescription>
							{openRemaining === 0
								? 'There are no steps left to do.'
								: `The ${openRemaining === 1 ? 'remaining step' : `${openRemaining} remaining steps`} will be deleted.`}{' '}
							Completed steps stay in the history.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Cancel</AlertDialogCancel>
						<AlertDialogAction
							onClick={confirmRemove}
							disabled={stopping}
							className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
						>
							Remove
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			<Dialog open={Boolean(editing)} onOpenChange={(open) => !open && setEditing(null)}>
				<DialogContent mobileVariant="fullscreen">
					<DialogHeader>
						<DialogTitle>Edit step</DialogTitle>
					</DialogHeader>
					{editing && (
						<ReminderComposer
							users={users}
							saving={saving}
							submitLabel="Save changes"
							creatorId={editing._user}
							initial={{
								text: editing.text,
								dateTime: editing.dateTime,
								notes: editing.notes ?? '',
								priority: editing.priority,
								notifications: editing.notifications,
								notificationOffsetMinutes: editing.notificationOffsetMinutes,
								_assignees: getReminderAssignees(editing).map((user) => user._id),
							}}
							hideRepeat
							onSubmit={saveEdit}
							onCancel={() => setEditing(null)}
						/>
					)}
				</DialogContent>
			</Dialog>
		</div>
	);
};

export default CustomerReminderSequences;
